/** Chase copy path (Tier 1 · A2, Ale ruling 2026-09-04) — the chase queue
 * renders every owed document from STRUCTURED data and never prints the
 * engine's task description. The engine keeps composing task titles and
 * descriptions exactly as today (5,356 pytest and the champion snapshots
 * pin them); this module only READS the deterministic title:
 *
 *   "Obtain evidence — <label> (<CODE>) — <object name>[, YYYY-MM-DD] [(recommended)]"
 *   "Obtain executed copy — <label> (<CODE>) — <object name> …"
 *   "Obtain evidence — <label> (<CODE>) on <object_type>"          (no name)
 *
 * and turns it into {code, label, object, date, optional}. Dates come out
 * in the one officer style ("20 May 2026"); "(recommended)" becomes the
 * `optional` flag (the word "recommended" never reaches the officer); the
 * counterparty sentinel "Unknown" the write path stores becomes null so
 * the queue can open a door instead of printing it.
 *
 * Display-only: nothing here feeds a write. */

/* Dependency-free on purpose: the unit lane compiles this file alone
 * (package.json test:unit) — the date style mirrors officerFormat
 * .formatOfficerDate exactly ("20 May 2026"). */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** ISO date / datetime → "20 May 2026"; unparseable passes through. */
export function houseDate(v: string | null | undefined): string | null {
  if (!v) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!m) return v
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
}

export type ChaseTitleKind = 'evidence' | 'executed_copy' | 'other'

export interface ParsedChaseTitle {
  kind: ChaseTitleKind
  /** The demand code in the title ("M1"); null when the title has none. */
  code: string | null
  /** The catalogue label as the title carries it ("Trade movement"). */
  label: string | null
  /** The object's name, without the ISO tail and the "(recommended)" suffix.
   *  Null when the title names only the object type ("… on trade"). */
  object: string | null
  /** The trailing effective date, in house style ("20 May 2026"). */
  date: string | null
  /** The date as the title carried it (ISO) — for sorting, never printed. */
  dateIso: string | null
  /** "(recommended)" in the title = a non-mandatory demand. */
  optional: boolean
}

const RECOMMENDED_RE = /\s*\(recommended\)\s*$/i
const TITLE_RE = /^Obtain (evidence|executed copy) — (.+?) \(([A-Z]\d{1,2}[a-z]?)\)(.*)$/
const ISO_TAIL_RE = /,\s*(\d{4}-\d{2}-\d{2})\s*$/

export function parseChaseTitle(title: string | null | undefined): ParsedChaseTitle {
  const raw = (title ?? '').trim()
  const optional = RECOMMENDED_RE.test(raw)
  const clean = raw.replace(RECOMMENDED_RE, '')
  const m = TITLE_RE.exec(clean)
  if (!m) {
    // Not the deterministic shape — keep the words, never the description.
    return {
      kind: 'other',
      code: null,
      label: null,
      object: clean || null,
      date: null,
      dateIso: null,
      optional,
    }
  }
  const kind: ChaseTitleKind = m[1] === 'evidence' ? 'evidence' : 'executed_copy'
  const rest = m[4]
  let object: string | null = null
  if (rest.startsWith(' — ')) object = rest.slice(3).trim() || null
  let dateIso: string | null = null
  if (object) {
    const d = ISO_TAIL_RE.exec(object)
    if (d) {
      dateIso = d[1]
      object = object.replace(ISO_TAIL_RE, '').trim() || null
    }
  }
  return {
    kind,
    code: m[3],
    label: m[2].trim(),
    object,
    date: dateIso ? houseDate(dateIso) : null,
    dateIso,
    optional,
  }
}

/** The plain demand name beside the code chip (mock chase-copy-path):
 *  "E2 register extract · B1 holder confirmation · M1 movement document
 *  · M2 cash movement · M3 authorization". Unknown codes fall back to the
 *  title's own label, lower-cased. */
export const CHASE_DEMAND_NAMES: Record<string, string> = {
  E1: 'constitutional document',
  E2: 'register extract',
  B1: 'holder confirmation',
  B2: 'commitment confirmation',
  B3: 'issued balance confirmation',
  M1: 'movement document',
  M2: 'cash movement',
  M3: 'authorization',
}

export function demandName(code: string | null, label: string | null): string {
  if (code && CHASE_DEMAND_NAMES[code]) return CHASE_DEMAND_NAMES[code]
  if (label) return label.charAt(0).toLowerCase() + label.slice(1)
  return 'document'
}

/** Accepted sources per code when the evidence-status detail has not
 *  loaded — the catalogue as migrated (115 + 129 + 130 + 164). */
export const FALLBACK_SOURCES: Record<string, string[]> = {
  E1: ['NOTARY', 'REGISTRY'],
  E2: ['REGISTRY'],
  B1: ['AGENT', 'REGISTRY', 'ISSUER'],
  B2: ['AGENT', 'REGISTRY', 'ISSUER'],
  B3: ['AGENT', 'REGISTRY', 'ISSUER', 'NOTARY'],
  M1: ['PARTY', 'ISSUER', 'NOTARY', 'AGENT'],
  M2: ['BANK'],
  M3: ['INTERNAL'],
}

/** "REGISTRY" → "registry": the short chip word. */
export function sourceChip(source: string): string {
  return source.trim().toLowerCase().replace(/_/g, ' ')
}

export function sourceChips(sources: readonly string[] | null | undefined, code: string | null) {
  const list = sources && sources.length ? sources : code ? (FALLBACK_SOURCES[code] ?? []) : []
  return list.map(sourceChip)
}

/** The write path stores "Unknown" when no counterparty could be named
 *  (investigations/service.py). The officer never reads that word: null
 *  means "no counterparty stored" and the queue opens a door. */
export function normaliseCounterparty(name: string | null | undefined): string | null {
  const s = (name ?? '').trim()
  if (!s) return null
  if (/^unknown$/i.test(s)) return null
  return s
}

/** Whole days since an ISO instant (never negative). */
export function ageDays(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.floor((now - t) / 86_400_000))
}

/* ── the object cell ───────────────────────────────────────────────────── */

export interface TradeParts {
  /** The movement kind as the title spells it ("acquisition"). */
  kind: string
  /** Units as the title spells them ("9,000", "-5,000,000"); null when absent. */
  units: string | null
  /** The instrument label; null when the title carries none. */
  instrument: string | null
}

const TRADE_RE = /^(.+?) of (-?[\d,]+(?:\.\d+)?) units? — (.+)$/
const TRADE_NO_INSTR_RE = /^(.+?) of (-?[\d,]+(?:\.\d+)?) units?$/

/** A trade name from display_names._trade_name:
 *  "<kind> of <units> units — <instrument>" → its parts; null otherwise. */
export function tradeParts(object: string | null | undefined): TradeParts | null {
  if (!object) return null
  const m = TRADE_RE.exec(object)
  if (m) return { kind: m[1].trim(), units: m[2], instrument: m[3].trim() }
  const n = TRADE_NO_INSTR_RE.exec(object)
  if (n) return { kind: n[1].trim(), units: n[2], instrument: null }
  return null
}

export type PlugKind = 'missing_movement' | 'inception_balance' | null

/** Officer plugs (Ale ruling 2026-09-04): a trade the officer or the
 *  engine booked to make the record tie out. The kind comes from the
 *  transaction type when a response carries it; otherwise from the name
 *  the title spells (kind_label of the same transaction type). */
export function plugKind(
  transactionType: string | null | undefined,
  objectName: string | null | undefined
): PlugKind {
  const t = (transactionType ?? '').toLowerCase()
  if (t === 'officer_reconciling_movement') return 'missing_movement'
  if (t === 'pre_existing_ownership') return 'inception_balance'
  const n = (objectName ?? '').toLowerCase()
  if (n.startsWith('officer reconciling movement')) return 'missing_movement'
  if (
    n.startsWith('inception balance') ||
    n.startsWith('pre existing ownership') ||
    n.startsWith('pre-existing ownership')
  )
    return 'inception_balance'
  return null
}

/** "+9,000" for a movement (a plain figure gains its sign; a negative keeps it). */
export function signedUnits(units: string | null): string | null {
  if (!units) return null
  return units.startsWith('-') || units.startsWith('+') ? units : `+${units}`
}

/** The special line under a plug row (ruled wording):
 *  missing movement → "+9,000 Namenaktien between <window>, booked by the
 *  officer on <date>" — the window is not on any response the queue reads
 *  today, so the line carries the booking date only;
 *  inception balance → "400 Namenaktien held at record start — balance
 *  document only". */
export function plugLine(kind: PlugKind, parts: TradeParts | null, date: string | null): string {
  if (kind === 'missing_movement') {
    const what = [signedUnits(parts?.units ?? null), parts?.instrument].filter(Boolean).join(' ')
    const booked = date ? `booked by the officer on ${date}` : 'booked by the officer'
    return what ? `${what}, ${booked}` : booked
  }
  if (kind === 'inception_balance') {
    const what = [parts?.units, parts?.instrument].filter(Boolean).join(' ')
    return what
      ? `${what} held at record start — balance document only`
      : 'held at record start — balance document only'
  }
  return ''
}

export type ObjectKind = 'entity' | 'position' | 'trade' | 'instrument' | 'asset' | 'other'

export function objectKind(objectType: string | null | undefined): ObjectKind {
  switch ((objectType ?? '').toLowerCase()) {
    case 'entity':
      return 'entity'
    case 'asset':
      return 'asset'
    case 'position':
      return 'position'
    case 'trade':
      return 'trade'
    case 'instrument':
      return 'instrument'
    default:
      return 'other'
  }
}

export interface ObjectCell {
  kind: ObjectKind
  /** The first line: the object's name in officer words. */
  head: string
  /** The second line (issuer / holder), when the title carries one. */
  sub: string | null
}

/** The object cell of a chase row, from the parsed title + object type.
 *  positions: "holder — instrument — issuer" → head "instrument", sub
 *  "holder · issuer"; instruments: "label — issuer" → head/sub; trades:
 *  "<kind> · <date> · <units> <instrument>"; entities: the name. Plug
 *  trades take their ruled name ("inception balance …"). */
export function objectCell(
  parsed: ParsedChaseTitle,
  objectType: string | null | undefined,
  plug: PlugKind
): ObjectCell | null {
  const kind = objectKind(objectType)
  const name = parsed.object
  if (!name) return null
  if (kind === 'trade' || (kind === 'other' && tradeParts(name))) {
    const parts = tradeParts(name)
    if (plug === 'inception_balance') {
      const what = parts ? [parts.units, parts.instrument].filter(Boolean).join(' ') : name
      return { kind: 'trade', head: `inception balance · ${what}`, sub: null }
    }
    if (plug === 'missing_movement') {
      const what = parts ? [parts.units, parts.instrument].filter(Boolean).join(' ') : name
      return {
        kind: 'trade',
        head: ['missing movement', parsed.date, what].filter(Boolean).join(' · '),
        sub: null,
      }
    }
    if (parts) {
      const what = [parts.units, parts.instrument].filter(Boolean).join(' ')
      return {
        kind: 'trade',
        head: [parts.kind, parsed.date, what].filter(Boolean).join(' · '),
        sub: null,
      }
    }
    return { kind: 'trade', head: [name, parsed.date].filter(Boolean).join(' · '), sub: null }
  }
  if (kind === 'position') {
    const bits = name.split(' — ').map((s) => s.trim())
    if (bits.length >= 2) {
      const [holder, instrument, issuer] = bits
      return {
        kind,
        head: instrument,
        sub: [holder, issuer].filter(Boolean).join(' · ') || null,
      }
    }
    return { kind, head: name, sub: null }
  }
  if (kind === 'instrument') {
    const bits = name.split(' — ').map((s) => s.trim())
    return { kind, head: bits[0], sub: bits.slice(1).join(' · ') || null }
  }
  return { kind, head: name, sub: null }
}
